import React, { useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import FormInput from "./index";
import useAccountValidation from "@/hooks/useAccountValidation";
import SVGCheckMark from "@/assets/icons/SVGCheckMark.tsx";
import SVGAlert from "@/assets/icons/SVGAlert.tsx";
import { validateAccountNumberFormat } from "@/utils/validateAccountNumber";
import { useUserStore } from "@/store/userStore";
import CustomerDataDisplay from "@/components/CustomerDataDisplay";

interface AccountNumberInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  zipCodeName?: string;
  onCustomerDataPopulated?: (customerData: any) => void;
  hideCustomerDataDisplay?: boolean;
  skipDuplicateCheck?: boolean;
  isSizingTool?: boolean;
  skipValidation?: boolean;
}

const AccountNumberInput: React.FC<AccountNumberInputProps> = ({
  name,
  label = "Account #",
  placeholder = "000000000000",
  disabled = false,
  zipCodeName,
  onCustomerDataPopulated,
  hideCustomerDataDisplay = false,
  skipDuplicateCheck = false,
  isSizingTool = false,
  skipValidation = false,
}) => {
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    control,
    trigger,
    formState,
  } = useFormContext();
  const accountNumber = watch(name);
  const zipCode = zipCodeName ? watch(zipCodeName) : undefined;
  const currentUser = useUserStore((state) => state.user);

  const fieldError = formState.errors[name];
  const hasManualError = fieldError && (fieldError as any).type === "manual";
  const lastValidatedRef = useRef<{
    accountNumber: string;
    zipCode?: string;
  } | null>(null);

  const { isValidating, validationResult, validateAccount, clearValidation } =
    useAccountValidation(500, skipDuplicateCheck, isSizingTool);

  useEffect(() => {
    if (skipValidation || hasManualError) return;

    const isSameAsLastValidation =
      lastValidatedRef.current?.accountNumber === accountNumber &&
      lastValidatedRef.current?.zipCode === zipCode;

    if (isSameAsLastValidation && validationResult) {
      return;
    }

    if (accountNumber) {
      lastValidatedRef.current = { accountNumber, zipCode };
      validateAccount(accountNumber, zipCode);
    } else {
      lastValidatedRef.current = null;
      clearValidation();
    }
  }, [
    accountNumber,
    validateAccount,
    clearValidation,
    skipValidation,
    zipCode,
    hasManualError,
  ]);

  const setCustomerDataFields = (customerData: any) => {
    setValue("customerName", customerData.customerName);
    setValue("address", customerData.address);
    setValue("city", customerData.city);
    setValue("state", customerData.state);

    setValue("accountEmail", customerData.email);

    if (customerData.address) {
      setValue("streetAddress", customerData.address);
    }

    const currentPayeeType = watch("payeeType");
    if (currentPayeeType === "customer") {
      if (customerData.mailingAddress) {
        setValue("payeeAddress", customerData.mailingAddress);
      }
      if (customerData.mailingCity) {
        setValue("payeeCity", customerData.mailingCity);
      }
      if (customerData.mailingState) {
        setValue("payeeState", customerData.mailingState);
      }
      if (customerData.mailingZipCode) {
        setValue("payeeZip", customerData.mailingZipCode);
      }
    }

    if (customerData.ratePlan) {
      setValue("ratePlan", customerData.ratePlan);
    }
    if (customerData.careEligible !== undefined) {
      setValue("careEligible", customerData.careEligible);
    }
    if (customerData.feraEligible !== undefined) {
      setValue("feraEligible", customerData.feraEligible);
    }
    if (customerData.cocEligible !== undefined) {
      setValue("cocEligible", customerData.cocEligible);
    }
  };

  useEffect(() => {
    if (hasManualError) return;
    if (!validationResult) return;

    const errorMessage = validationResult.error;
    const isZipCodeMismatch = validationResult.zipCodeMatch === false;
    const errorField = isZipCodeMismatch && zipCodeName ? zipCodeName : name;

    if (validationResult.isValid && validationResult.customerData) {
      clearErrors(name);
      if (zipCodeName) clearErrors(zipCodeName);
      setCustomerDataFields(validationResult.customerData);
      trigger(["customerName"]);
      onCustomerDataPopulated?.(validationResult.customerData);
    } else if (!validationResult.isValid) {
      if (isZipCodeMismatch && validationResult.customerData) {
        clearErrors(name);
        setCustomerDataFields(validationResult.customerData);
        trigger(["customerName"]);
        onCustomerDataPopulated?.(validationResult.customerData);
        setError(errorField, { message: errorMessage });
      } else {
        setError(errorField, { message: errorMessage });
        if (isZipCodeMismatch) clearErrors(name);
      }
    }
  }, [
    validationResult,
    setValue,
    setError,
    clearErrors,
    name,
    zipCodeName,
    onCustomerDataPopulated,
    trigger,
    hasManualError,
  ]);

  const getAdornment = () => {
    if (hasManualError) {
      return <SVGAlert />;
    }

    if (isValidating) {
      return <CircularProgress size={16} />;
    }

    if (validationResult) {
      if (validationResult.customerData) {
        return <SVGCheckMark />;
      } else if (!validationResult.isValid) {
        return <SVGAlert />;
      }
    }

    return null;
  };

  return (
    <Box sx={{ position: "relative" }}>
      <FormInput
        name={name}
        control={control}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        adornment={getAdornment()}
        controlProps={{
          rules: {
            required: "Required",
            validate: (value: string) => {
              const result = validateAccountNumberFormat(value);
              return result.valid || result.message;
            },
          },
        }}
      />
      {validationResult?.isValid && validationResult.customerData && (
        <>
          <Typography
            variant="caption"
            sx={{
              color: "success.main",
              fontSize: "12px",
              marginTop: "4px",
              display: "block",
            }}
          >
            ✓ Customer data populated from account number
          </Typography>
          {!hideCustomerDataDisplay && (
            <CustomerDataDisplay customerData={validationResult.customerData} />
          )}
        </>
      )}
      {validationResult &&
        !validationResult.isValid &&
        validationResult.error && (
          <Typography
            sx={{
              fontSize: "12px",
              color: "error.main",
              paddingTop: "4px",
            }}
          >
            {validationResult.error}
          </Typography>
        )}
    </Box>
  );
};

export default AccountNumberInput;
